import { IDrawableObject, RenderSettings, ObjectInteraction } from "./ObjectManager";
import { Context } from "../Engine";
import { Camera } from "../View";
import { PropertyAnimation } from "@spi/Common/Animation";
import { VertexBuffer } from "./VertexBuffer";
import { ITexture } from "../Texture";
import { AdditiveBlendShaderSettings } from "../Shader/Generated/AdditiveBlendShader.generated";
import { vec2 } from "gl-matrix";
import { VolumetricLightShaderSettings } from "../Shader/Generated/VolumetricLightShader.generated";
import { TextureOverlayShaderSettings } from "../Shader/Generated/TextureOverlayShader.generated";

// See: https://stackoverflow.com/questions/24104939/rendering-a-fullscreen-quad-using-webgl

export abstract class Overlay implements IDrawableObject {
    order: 99999;

    animatedProperties: PropertyAnimation;
    interaction: ObjectInteraction;

    protected buffer: VertexBuffer;

    constructor(private ctx: Context) {
        this.createGeometry();
    }

    render(ctx: Context, camera: Camera, settings: RenderSettings): void {
        const gl = ctx.gl;

        this.renderImpl(ctx);

        // Draw 6 vertexes => 2 triangles:
        gl.drawArrays(gl.TRIANGLES, 0, 6);
    }

    protected abstract renderImpl(ctx: Context): void;

    private createGeometry() {
        let gl = this.ctx.gl;

        let verts = [
            // First triangle:
            1.0, 1.0,
            -1.0, 1.0,
            -1.0, -1.0,
            // Second triangle:
            -1.0, -1.0,
            1.0, -1.0,
            1.0, 1.0
        ];


        this.buffer = new VertexBuffer(gl.createBuffer(), gl.TRIANGLES, 0, 0);

        gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer.glVertexBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(verts), gl.DYNAMIC_DRAW);

        this.buffer.itemSize = 2;
        this.buffer.itemCount = 2;
    }
}

export class AdditiveBlendOverlay extends Overlay {

    private get hasAlpha(): boolean {
        return this.alpha !== undefined && this.alpha !== null;
    }

    constructor(ctx: Context, public blendTexture: ITexture, public sourceTexture?: ITexture, public alpha?: number) {
        super(ctx);

    }

    protected getSettings(): AdditiveBlendShaderSettings {
        let settings: AdditiveBlendShaderSettings = [];
        if (this.hasAlpha) {
            settings.push('SET_ALPHA');
        }
        if (this.sourceTexture) {
            settings.push('USE_SOURCE_TEXTURE');
        }
        return settings;
    }

    protected renderImpl(ctx: Context): void {
        const gl = ctx.gl;

        let shader = ctx.engine.shaderManager.getAdditiveBlendShader(...this.getSettings());
        ctx.setShader(shader);

        gl.useProgram(shader.program);

        gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer.glVertexBuffer);
        gl.vertexAttribPointer(shader.attributes.aVertexPosition, 2, gl.FLOAT, false, 0, 0);

        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, this.blendTexture.glTexture);
        gl.uniform1i(shader.uniforms.uBlendTextureSampler, 0); // bind texture0

        if (this.sourceTexture) {
            gl.activeTexture(gl.TEXTURE1);
            gl.bindTexture(gl.TEXTURE_2D, this.sourceTexture.glTexture);
            gl.uniform1i(shader.uniforms.uSourceTextureSampler, 1); // bind texture0
        }

        if (this.hasAlpha) {
            gl.uniform1f(shader.uniforms.uAlpha, this.alpha);
        }
    }
}

export class TextureOverlay extends Overlay {

    constructor(ctx: Context, public texture: ITexture) {
        super(ctx);
    }

    protected getSettings(): TextureOverlayShaderSettings {
        let settings: TextureOverlayShaderSettings = [];

        return settings;
    }

    protected renderImpl(ctx: Context): void {
        const gl = ctx.gl;

        let shader = ctx.engine.shaderManager.getTextureOverlayShader(...this.getSettings());
        ctx.setShader(shader);

        gl.useProgram(shader.program);

        gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer.glVertexBuffer);
        gl.vertexAttribPointer(shader.attributes.aVertexPosition, 2, gl.FLOAT, false, 0, 0);

        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, this.texture.glTexture);
        gl.uniform1i(shader.uniforms.uTextureSampler, 0); // bind texture0
    }
}

export class VolumetricLightOverlay extends Overlay {

    constructor(ctx: Context, public blendTexture: ITexture, public settings?: VolumetricLightSettings) {
        super(ctx);
        this.setDefaults();
    }

    private setDefaults() {
        if (!this.settings) {
            this.settings = {};
        }

        if (this.settings.lightPosition === undefined) {
            this.settings.lightPosition = vec2.fromValues(0.5, 0.5);
        }
        if (this.settings.decay === undefined) {
            this.settings.decay = 0.90;
        }
        if (this.settings.density === undefined) {
            this.settings.density = 0.6;
        }
        if (this.settings.exposure === undefined) {
            this.settings.exposure = 0.5;
        }
        if (this.settings.samples === undefined) {
            this.settings.samples = 50;
        }
        if (this.settings.weight === undefined) {
            this.settings.weight = 0.2;
        }
    }

    protected getSettings(): VolumetricLightShaderSettings {
        let settings: VolumetricLightShaderSettings = [];
        return settings;
    }

    protected renderImpl(ctx: Context): void {
        const gl = ctx.gl;

        let shader = ctx.engine.shaderManager.getVolumetricLightShader(...this.getSettings());
        ctx.setShader(shader);

        gl.useProgram(shader.program);

        gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer.glVertexBuffer);
        gl.vertexAttribPointer(shader.attributes.aVertexPosition, 2, gl.FLOAT, false, 0, 0);

        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, this.blendTexture.glTexture);
        gl.uniform1i(shader.uniforms.uBlendTextureSampler, 0); // bind texture0

        gl.uniform2fv(shader.uniforms.uLightPosition, this.settings.lightPosition);
        gl.uniform1i(shader.uniforms.uSamples, this.settings.samples);
        gl.uniform1f(shader.uniforms.uWeight, this.settings.weight);
        gl.uniform1f(shader.uniforms.uExposure, this.settings.exposure);
        gl.uniform1f(shader.uniforms.uDensity, this.settings.density);
        gl.uniform1f(shader.uniforms.uDecay, this.settings.decay);
    }
}

type VolumetricLightSettings = {
    lightPosition?: vec2 | number[],
    exposure?: number,
    decay?: number,
    density?: number,
    weight?: number,
    samples?: number
};